import React, { useEffect, useState } from 'react';
import { auditAPI } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';

const actionColors = {
  CREATE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  UPDATE: 'bg-blue-50 text-blue-700 border-blue-200',
  DELETE: 'bg-red-50 text-red-700 border-red-200',
  UPLOAD: 'bg-violet-50 text-violet-700 border-violet-200',
  STATUS_CHANGE: 'bg-amber-50 text-amber-700 border-amber-200',
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');

  useEffect(() => {
    loadLogs();
  }, [entityFilter]);

  const loadLogs = async () => {
    try {
      const params = { limit: 200 };
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      const res = await auditAPI.list(params);
      setLogs(res.data);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="audit-log-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">Audit Log</h1>
          <p className="text-sm text-slate-500 mt-0.5">Complete history of all system changes</p>
        </div>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-40 h-9" data-testid="audit-entity-filter">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            <SelectItem value="tool">Tools</SelectItem>
            <SelectItem value="calibration">Calibrations</SelectItem>
            <SelectItem value="file">Files</SelectItem>
            <SelectItem value="user">Users</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="text-xs font-semibold text-slate-500 w-[170px]">Timestamp</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 w-[100px]">Action</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 w-[80px]">Entity</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 w-[120px]">User</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-sm text-slate-400">Loading...</TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-sm text-slate-400">No audit log entries</TableCell>
              </TableRow>
            ) : logs.map(log => (
              <TableRow key={log.id} data-testid={`audit-entry-${log.id}`} className="hover:bg-slate-50/50">
                <TableCell className="font-mono text-[11px] text-slate-500 whitespace-nowrap">
                  {log.timestamp?.replace('T', ' ').substring(0, 19)}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] ${actionColors[log.action] || 'bg-slate-50 text-slate-600'}`}>
                    {log.action}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-slate-500 capitalize">{log.entity_type}</TableCell>
                <TableCell className="text-xs font-medium text-[#0F172A]">{log.user_name}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-md truncate">{log.details}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
